import { liveCasinoViewTypesEnum } from '../enums/live-casino-view-types.enum';
import { StringUtilService } from './util/string-util.service';

export const LiveCasinoService = {
    /**
   * Converts hours from UTC to hours in current timezone
   * @param startTime UTC hours in 00:00 format
   */
    getStartTimeInCurrentZone(startTime) {
        const [hours, minutes] = startTime.split(':');
        const now = new Date();
        const startDateTimeLocal = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes));

        return `${startDateTimeLocal.getHours()}:${(startDateTimeLocal
            .getMinutes() < 10 ? '0' : '')}${startDateTimeLocal.getMinutes()}`;
    },

    /**
     * Checks if passed provider is Evolution.
     * @param providerName Pass provider for CMS brand, and sub-provider otherwise.
     * @return {boolean} Weather (sub)provider is Evolution.
     */
    isEvolutionGame(providerName) {
        const evolutionProviderNameOptions = [
            'evolution',
            'evolution gaming',
        ];

        return providerName && evolutionProviderNameOptions.includes(providerName.toLowerCase());
    },

    getGridItemBadges({
        Dictionary,
        isLoggedIn,
        game,
        recentGames,
        betLimits,
        liveCasinoState,
        currencyCode,
        currencySign,
    }) {
        const badges = [];
        const gameProvider = game?.isCmsGame ? game.externalGameProvider : game.externalGameSubProvider;
        const gameType = this.getGameViewType(gameProvider, liveCasinoState?.viewType);

        /** badges **/
        // bet behind
        if (
            this.isBetBehind(gameType, liveCasinoState)
            && Array.isArray(liveCasinoState?.seatsTaken)
            && liveCasinoState?.seatsTaken?.length >= 7
        ) {
            badges.push({
                badgeName: 'bet-behind',
                badgeText: Dictionary.translate('betBehind'),
            });
        }

        // occupied
        if (!this.isBetBehind(gameType, liveCasinoState)
            && Array.isArray(liveCasinoState?.seatsTaken)
            && liveCasinoState?.seatsTaken?.length >= 7
        ) {
            badges.push({
                badgeName: 'occupied',
                badgeText: Dictionary.translate('occupied'),
            });
        }

        // last seat
        if (
            gameType === liveCasinoViewTypesEnum.BLACKJACK
            && Array.isArray(liveCasinoState?.seatsTaken)
            && liveCasinoState?.seatsTaken?.length === 6
        ) {
            badges.push({
                badgeName: 'last-seat',
                badgeText: Dictionary.translate('lastSeat'),
            });
        }

        // open
        if (this.isGameOpen(gameType, liveCasinoState)) {
            badges.push({
                badgeName: 'open',
                badgeText: Dictionary.translate('open'),
            });
        }

        // new
        if (this.isNewGame(gameType, isLoggedIn, game, liveCasinoState)) {
            badges.push({
                badgeName: 'new',
                badgeText: Dictionary.translate('newBanner'),
            });
        }

        // players count
        if (this.isPlayersBadgeAllowed(gameType, liveCasinoState)) {
            badges.push({
                badgeName: 'players',
                badgeText: liveCasinoState?.players,
            });
        }

        // bet limits
        const betLimitsString = this.getBetLimitsString(betLimits, currencyCode, currencySign);
        if (betLimitsString) {
            badges.push({
                badgeName: 'bet-limits',
                badgeText: betLimitsString,
            });
        }

        // played
        if (isLoggedIn && Array.isArray(recentGames) && recentGames.includes(game.id)) {
            badges.push({
                badgeName: 'played',
                badgeText: Dictionary.translate('played'),
            });
        }

        return badges;
    },

    getGameViewType(gameProvider, gameType) {
        if (!gameType) {
            return null;
        }

        if (this.isEvolutionGame(gameProvider)) {
            if (Object.values(liveCasinoViewTypesEnum).includes(gameType)) {
                return gameType;
            }

            return liveCasinoViewTypesEnum.GENERAL;
        }
    },

    isBetBehind(gameType, liveCasinoState) {
        return liveCasinoState
            && gameType === liveCasinoViewTypesEnum.BLACKJACK
            && liveCasinoState?.betBehind === true;
    },

    isGameOpen(gameType, liveCasinoState) {
        // specific open game logic for blackjack
        if (
            gameType === liveCasinoViewTypesEnum.BLACKJACK
        ) {
            return liveCasinoState?.players <= 3;
        }

        // for all others - count same players amount
        return liveCasinoState?.players <= 9;
    },

    isPlayersBadgeAllowed(gameType, liveCasinoState) {
        // specific logic for blackjack
        if (
            gameType === liveCasinoViewTypesEnum.BLACKJACK
            && liveCasinoState?.players >= 8
        ) {
            return true;
        }

        // for all others - count same players amount
        return liveCasinoState?.players >= 10;
    },

    isNewGame(gameType, isLoggedIn, game, liveCasinoState) {
        if (!isLoggedIn && game.gameConfig.includes('New')) {
            // specific logic for blackjack
            if (
                gameType === liveCasinoViewTypesEnum.BLACKJACK
                && liveCasinoState?.players <= 5
            ) {
                return true;
            }

            if (gameType !== liveCasinoViewTypesEnum.BLACKJACK) {
                return true;
            }
        }

        return false;
    },

    getBetLimitsString(betLimits, currencyCode, currencySign) {
        if (betLimits) {
            const currencySignDecoded = StringUtilService.decodeHtmlEntity(currencySign);
            const betLimitsForCurrency = betLimits?.betLimits?.[currencyCode];

            if (!betLimitsForCurrency?.min && !betLimitsForCurrency?.max) {
                return null;
            }

            return `${currencySignDecoded}${betLimitsForCurrency?.min}-${betLimitsForCurrency?.max}`;
        }

        return null;
    },

    getOpensInTime(startTime) {
        const [hours, minutes] = startTime.split(':');
        const now = new Date();
        const startDateTimeLocal = new Date(Date.UTC(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            hours,
            minutes
        ));

        const differenceMs = startDateTimeLocal - now;

        // 1- Convert to seconds:
        let secondsLeft = differenceMs / 1000;

        // 2- Extract hours:
        const hoursLeft = parseInt( secondsLeft / 3600, 10 ); // 3,600 seconds in 1 hour
        secondsLeft = secondsLeft % 3600; // seconds remaining after extracting hours

        // 3- Extract minutes:
        const minutesLeft = parseInt( secondsLeft / 60, 10 ); // 60 seconds in 1 minute

        // 4- Keep only seconds not extracted to minutes:
        secondsLeft = secondsLeft % 60;

        return {
            h: hoursLeft,
            m: minutesLeft,
            s: secondsLeft,
        };
    },
};
